// @ts-check
/**
 * crack-canvas.js — Glass-shatter entrance animation.
 *
 * The canvas draws a field of irregular glass shards that originate at an
 * impact point and spread outward. The animation transitions from an intact
 * glass surface through impact to the current shattered result state:
 *
 *   Phase 0 (0–8%):     Intact frosted-glass surface fades in.
 *   Phase 1 (8–14%):    Impact flash — bright radial burst at strike point.
 *   Phase 2 (14–50%):   Crack lines propagate from the centre outward over
 *                        the intact glass; inner shard edges begin to show.
 *   Phase 3 (50–100%):  Glass fractures — shards individualise, stroke edges
 *                        fade in, shards push outward (0.97→1.00 scale) to
 *                        reach the current result state.
 *
 * After the animation completes the cracks stay visible and fade out as the
 * user scrolls past the hero.
 */

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 *
 * @typedef {Object} Shard
 * @property {Point[]} finalVerts
 * @property {number} delay      — 0..1 delay before this shard starts growing
 * @property {number} fillOpacity
 * @property {number} strokeOpacity
 * @property {number} lineWidth
 *
 * @typedef {Object} CrackLine
 * @property {Point[]} points    — vertices of the crack path (centre → outer)
 * @property {number} length     — total path length for proportional drawing
 * @property {number} delay      — 0..1 when this line starts drawing
 * @property {number} lineWidth  — stroke width (thinner on right for balance)
 * @property {number} opacity    — stroke opacity (darker on right for balance)
 */

/**
 * @param {HTMLCanvasElement} canvas
 * @param {Object} [options]
 * @param {{x:number, y:number}} [options.impact] — normalised coords within
 *   `frame` (0-1), i.e. the reference composition, not the raw viewport
 * @param {{x:number, y:number}} [options.impactScreen] — the burst point in
 *   raw viewport px. Preferred over `impact` when given: the subject's screen
 *   position is weighted independently of the design frame (a portrait
 *   viewport clamps it inward), so a frame-relative point would drift off the
 *   sculpture. SpiritStage.heroSubjectScreenRect() supplies it.
 * @param {{x:number, y:number, w:number, h:number}} [options.frame] — the
 *   reference frame's rect in viewport px, as reported by SpiritStage. The
 *   shards are authored against this frame, so on a taller-than-reference
 *   phone they must fill it rather than the whole screen — otherwise the
 *   glass keeps bursting across 844px while the sculpture behind it has
 *   shrunk to a fraction of that. Defaults to the full viewport.
 * @param {number} [options.duration] — total animation duration in ms (default 1400)
 * @param {number} [options.shardCount] — approximate number of shards (default 48)
 * @param {number} [options.scale] — the page's shared geometric ratio, used to
 *   size the burst core in proportion with the sculpture (default 1)
 * @param {boolean} [options.reducedMotion] — draw static final frame, no animation
 * @param {() => void} [options.onComplete]
 * @returns {{ destroy: () => void }}
 */
export function drawCracks(
  canvas,
  {
    impact = { x: 0.5, y: 0.5 },
    impactScreen,
    frame,
    duration = 1400,
    shardCount = 64,
    scale = 1,
    reducedMotion = false,
    onComplete,
  } = {}
) {
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth;
  const h = window.innerHeight;

  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { destroy() {} };
  }
  ctx.scale(dpr, dpr);

  // The composition frame the cracks are authored in. Falls back to the whole
  // viewport, which is exactly the desktop reference case.
  const bx0 = frame ? frame.x : 0;
  const by0 = frame ? frame.y : 0;
  const bx1 = frame ? frame.x + frame.w : w;
  const by1 = frame ? frame.y + frame.h : h;

  // The burst origin. An explicit screen point wins: it comes from the
  // sculpture's projected box, so it stays on the marble whatever the subject's
  // position weight did. Otherwise it is authored inside the frame.
  const cx = impactScreen ? impactScreen.x : bx0 + (bx1 - bx0) * impact.x;
  const cy = impactScreen ? impactScreen.y : by0 + (by1 - by0) * impact.y;

  // ── Deterministic PRNG ──────────────────────────────────────────────────
  let seed = 42;
  const rand = () => {
    seed = (seed * 16807 + 12345) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  // ── Boundary distances ──────────────────────────────────────────────────
  /**
   * Distance along `angle` from the impact point to a rectangle's edge.
   * @param {number} angle
   * @param {number} x0 @param {number} y0
   * @param {number} x1 @param {number} y1
   * @returns {number}
   */
  function exitDistance(angle, x0, y0, x1, y1) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    let t = Infinity;
    if (cos > 0) t = Math.min(t, (x1 - cx) / cos);
    else if (cos < 0) t = Math.min(t, (x0 - cx) / cos);
    if (sin > 0) t = Math.min(t, (y1 - cy) / sin);
    else if (sin < 0) t = Math.min(t, (y0 - cy) / sin);
    return t === Infinity ? Math.max(x1 - x0, y1 - y0) : t;
  }

  /**
   * How far a shard may reach: all the way to the SCREEN edge, not just to the
   * composition frame. The frame is where the sculpture and type live; on a
   * portrait phone it is a 390×244 band, and everything outside it is empty
   * screen. Letting the glass run to the screen edge is what makes it fill
   * that void instead of stopping short and leaving a dead lower half.
   * On a landscape viewport frame and screen coincide, so nothing changes.
   */
  const screenExit = (angle) => exitDistance(angle, 0, 0, w, h);
  /** Where the composition ends — only used to weigh ray directions. */
  const frameExit = (angle) => exitDistance(angle, bx0, by0, bx1, by1);

  // ── Generate radial shards ──────────────────────────────────────────────
  /** @type {number[]} */
  const rays = [];
  // Rays to the screen's four corners guarantee coverage to the edges.
  const corners = [
    Math.atan2(-cy, -cx),
    Math.atan2(-cy, w - cx),
    Math.atan2(h - cy, w - cx),
    Math.atan2(h - cy, -cx),
  ];
  rays.push(...corners);

  // ── Adaptive ray distribution ───────────────────────────────────────────
  // Rays are not sprinkled uniformly: each direction is weighted by the EMPTY
  // space that lies beyond the composition frame. A portrait hero has ~400px
  // of unused screen below the sculpture and almost none above the nav, so
  // shards are dealt out mostly downward and the empty band fills itself in.
  // On a landscape viewport every direction weighs the same (no room beyond
  // the frame anywhere), which reproduces the even, radial desktop burst.
  const BINS = 144;
  const roomOf = (angle) => Math.max(0, screenExit(angle) - frameExit(angle));
  const diagonal = Math.max(1, Math.hypot(w, h));
  const binWeights = new Float64Array(BINS);
  let weightTotal = 0;
  for (let i = 0; i < BINS; i++) {
    const angle = ((i + 0.5) / BINS) * Math.PI * 2 - Math.PI;
    // The +1 floor keeps shards all round, so the burst still reads as a
    // burst rather than collapsing into one long streak.
    const weight = 1 + (roomOf(angle) / diagonal) * 12;
    binWeights[i] = weight;
    weightTotal += weight;
  }
  /** Importance-sampling: dense where there is room, sparse where there is not. */
  function sampleAngle() {
    let pick = rand() * weightTotal;
    for (let i = 0; i < BINS; i++) {
      pick -= binWeights[i];
      if (pick <= 0) return ((i + rand()) / BINS) * Math.PI * 2 - Math.PI;
    }
    return 0;
  }

  const extraRays = Math.max(shardCount - corners.length, 0);
  for (let i = 0; i < extraRays; i++) {
    rays.push(sampleAngle());
  }
  rays.sort((a, b) => a - b);
  // Duplicate first ray at +2π so the final sector wraps cleanly.
  rays.push(rays[0] + Math.PI * 2);

  // Add extra rays on the right side for bilateral balance (thinner, darker).
  // Right side = angles roughly -π/2 to π/2 (pointing right).
  const rightSideExtra = Math.floor(extraRays * 0.3);
  for (let i = 0; i < rightSideExtra; i++) {
    const angle = sampleAngle();
    rays.push(-Math.PI / 2 + Math.abs(angle % Math.PI));
  }
  rays.sort((a, b) => a - b);
  rays.push(rays[0] + Math.PI * 2);

  const maxDist = Math.max(...corners.map((a) => screenExit(a))) * 1.05;

  /** @type {Shard[]} */
  const shards = [];

  /** @type {CrackLine[]} */
  const crackLines = [];

  for (let i = 0; i < rays.length - 1; i++) {
    const a0 = rays[i];
    const a1 = rays[i + 1];
    const sector = a1 - a0;
    if (sector < 0.001) continue;

    const mid = a0 + sector * 0.5;

    // Vary the outer reach of each shard so the pattern feels organic.
    // Outer radius scaled by distance from centre: centre shards stay small,
    // outer shards reach further — creates a clear size hierarchy.
    const distRatio = (screenExit(mid) / maxDist);
    const outerScale = 0.3 + distRatio * 0.45;
    // The core radius follows the sculpture, so the dense centre stays in
    // proportion with the model instead of staying desktop-sized around a
    // shrunken head.
    const inner = (6 + rand() * 14) * (0.5 + 0.5 * scale);
    const outer0 = screenExit(a0) * outerScale * (0.7 + rand() * 0.6);
    const outerMid = screenExit(mid) * outerScale * (0.75 + rand() * 0.55);
    const outer1 = screenExit(a1) * outerScale * (0.7 + rand() * 0.6);

    // Optional internal fracture point makes shards look cracked rather than
    // like perfect pie slices.
    /** @type {{angle:number, dist:number}|null} */
    const fracture =
      rand() > 0.35
        ? {
            angle: mid + (rand() - 0.5) * sector * 0.35,
            dist: outerMid * (0.3 + rand() * 0.35),
          }
        : null;

    /** @type {Point[]} */
    const finalVerts = [
      // Slightly off-centre inner point.
      {
        x: cx + Math.cos(mid) * inner * 0.25,
        y: cy + Math.sin(mid) * inner * 0.25,
      },
      // Inner edge on ray a0.
      { x: cx + Math.cos(a0) * inner, y: cy + Math.sin(a0) * inner },
      // Outer edge on ray a0.
      { x: cx + Math.cos(a0) * outer0, y: cy + Math.sin(a0) * outer0 },
    ];

    if (fracture) {
      finalVerts.push({
        x: cx + Math.cos(fracture.angle) * fracture.dist,
        y: cy + Math.sin(fracture.angle) * fracture.dist,
      });
    }

    finalVerts.push(
      // Outer edge on ray a1.
      { x: cx + Math.cos(a1) * outer1, y: cy + Math.sin(a1) * outer1 },
      // Inner edge on ray a1.
      { x: cx + Math.cos(a1) * inner, y: cy + Math.sin(a1) * inner }
    );

    // Delay is driven by distance from the impact: centre shards break first.
    let avgX = 0;
    let avgY = 0;
    for (const v of finalVerts) {
      avgX += v.x;
      avgY += v.y;
    }
    avgX /= finalVerts.length;
    avgY /= finalVerts.length;
    const dist = Math.hypot(avgX - cx, avgY - cy);
    const delay = Math.min(0.72, (dist / maxDist) * 0.68);

    // Check if this shard is on the right side (for bilateral balance).
    const isRightSide = mid > -Math.PI / 2 && mid < Math.PI / 2;

    shards.push({
      finalVerts,
      delay,
      // Right-side shards are thinner and darker for visual balance.
      fillOpacity: isRightSide ? (0.08 + rand() * 0.10) : (0.12 + rand() * 0.15),
      strokeOpacity: isRightSide ? (0.55 + rand() * 0.20) : (0.75 + rand() * 0.25),
      lineWidth: isRightSide ? (0.8 + rand() * 1.2) : (1.2 + rand() * 2.0),
    });

    // Generate crack lines from centre to outer vertices of this shard.
    // Each shard contributes 1–2 crack lines depending on fracture presence.
    const outerVerts = finalVerts.filter((_, idx) => idx === 2 || idx === finalVerts.length - 2);
    for (const ov of outerVerts) {
      const points = [
        { x: cx, y: cy },
        { x: ov.x, y: ov.y },
      ];
      const segLen = Math.hypot(ov.x - cx, ov.y - cy);
      // Right-side crack lines are thinner for visual balance.
      crackLines.push({
        points,
        length: segLen,
        delay,
        lineWidth: isRightSide ? 1.2 : 1.8,
        opacity: isRightSide ? 0.7 : 0.9,
      });
    }
    if (fracture) {
      const fp = fracture;
      const fPt = finalVerts.find(
        (v) => Math.abs(v.x - (cx + Math.cos(fp.angle) * fp.dist)) < 1 &&
                Math.abs(v.y - (cy + Math.sin(fp.angle) * fp.dist)) < 1
      );
      if (fPt) {
        crackLines.push({
          points: [{ x: cx, y: cy }, { x: fPt.x, y: fPt.y }],
          length: Math.hypot(fPt.x - cx, fPt.y - cy),
          delay,
        });
      }
    }
  }

  // ── Easing & drawing ────────────────────────────────────────────────────
  const easeOutQuart = (/** @type {number} */ t) => 1 - Math.pow(1 - t, 4);

  /**
   * Animation phases (fraction of total progress 0→1):
   *   Phase 0 — 0.00–0.08  Intact glass surface fades in (frosted pane)
   *   Phase 1 — 0.08–0.14  Impact flash at the strike point
   *   Phase 2 — 0.14–0.50  Crack lines propagate from centre outward
   *   Phase 3 — 0.50–1.00  Glass fractures — shards individualise + separate
   *                         into the current result state.
   *
   * Result state (progress = 1) is IDENTICAL to the previous `drawFrame(1)`.
   */
  const T_GLASS  = 0.08;
  const T_FLASH  = 0.06; // duration of flash, starts at T_GLASS
  // Phase 2 (shard shatter-in) spans the remaining progress, no intermediate
  // crack-only phase — shards render with full fill + stroke from the start.

  /**
   * Draws a radial gradient glow at the impact centre.
   * @param {number} alpha
   * @param {number} radius
   */
  function drawImpactGlow(alpha, radius) {
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, `rgba(255,255,255,${alpha})`);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  }

  /**
   * @param {number} progress — 0..1 global animation progress
   */
  function drawFrame(progress) {
    ctx.clearRect(0, 0, w, h);

    // ────────────────────────────────────────────────────────────────────────
    // Phase 0: Tension glow builds (progress 0 → T_GLASS)
    // No glass surface yet — shards appear together in Phase 2.
    // ────────────────────────────────────────────────────────────────────────
    if (progress < T_GLASS) {
      const t = easeOutQuart(progress / T_GLASS);
      drawImpactGlow(0.12 * t, 6 + t * 14);
      return;
    }

    // ────────────────────────────────────────────────────────────────────────
    // Phase 1: Impact flash (progress T_GLASS → T_GLASS + T_FLASH)
    // ────────────────────────────────────────────────────────────────────────
    if (progress < T_GLASS + T_FLASH) {
      const ft = (progress - T_GLASS) / T_FLASH;
      const fa = 0.65 * (1 - easeOutQuart(ft));
      drawImpactGlow(fa, 4 + ft * 40);
      return;
    }

    // ────────────────────────────────────────────────────────────────────────
    // Phase 2: Shards shatter in — full rendering from the start
    //          (progress T_GLASS + T_FLASH → 1.0)
    // Each shard fades in with individual fill + stroke + scale, eliminating
    // the old "crack skeleton first, full fragments later" two-step.
    // ────────────────────────────────────────────────────────────────────────
    const shatterT = (progress - T_GLASS - T_FLASH) / (1 - T_GLASS - T_FLASH);

    // Brief flash tail.
    if (shatterT < 0.06) {
      const tail = shatterT / 0.06;
      drawImpactGlow(0.30 * (1 - tail), 4 + tail * 30);
    }

    // Subtle centre glow that fades as shards solidify.
    if (shatterT < 0.5) {
      const g = 1 - shatterT / 0.5;
      drawImpactGlow(0.08 * g, 10);
    }

    // Forward: slow centre-out spread over the full entrance duration.
    drawShardsAt(shatterT, true, 0.5);
  }

  /**
   * Draws shards at a given shatter progress (0 = none, 1 = fully shattered).
   *
   * In forward entrance (drawFrame) the stagger multiplier is 0.5 so shards
   * spread out gradually from centre → edge over the full animation duration.
   *
   * In scroll-driven exit the same stagger is amplified to 1.0 so outer shards
   * disappear much faster — creating a decisive "shrink toward impact point"
   * look rather than a stretched fade.
   *
   * @param {number} t — 0..1 shatter progress
   * @param {boolean} [stagger] — apply centre-out delay (default false)
   * @param {number} [strength] — stagger multiplier (forward 0.5, exit 1.0)
   */
  function drawShardsAt(t, stagger, strength = 0.5) {
    for (const shard of shards) {
      let localT;
      if (stagger) {
        const s = Math.min(strength, 0.99); // keep (1 - s*delay) positive
        localT = Math.max(0, Math.min(1, (t - shard.delay * s) / (1 - shard.delay * s)));
      } else {
        // All shards fade together — no spatial effect.
        localT = Math.max(0, Math.min(1, t));
      }
      if (localT <= 0) continue;

      const e = easeOutQuart(localT);

      // Scale: subtle outward push as shard separates.
      const scale = 0.94 + 0.06 * e;

      ctx.beginPath();
      for (const v of shard.finalVerts) {
        const x = cx + (v.x - cx) * scale;
        const y = cy + (v.y - cy) * scale;
        ctx.lineTo(x, y);
      }
      ctx.closePath();

      // Individual fill opacity from the start — no uniform step.
      ctx.fillStyle = `rgba(255,255,255,${shard.fillOpacity * e})`;
      ctx.fill();

      // Stroke appears together with fill, not delayed.
      ctx.strokeStyle = `rgba(255,255,255,${shard.strokeOpacity * e})`;
      ctx.lineWidth = shard.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  }

  // ── Animation loop ──────────────────────────────────────────────────────
  let rafId = 0;
  /** Whether the forward auto-play has finished. */
  let autoPlayDone = false;

  /**
   * @param {number} now
   */
  function animate(now) {
    const elapsed = now - startTime;
    const t = Math.max(0, Math.min(elapsed / duration, 1));
    drawFrame(t);

    if (t < 1) {
      rafId = requestAnimationFrame(animate);
    } else {
      autoPlayDone = true;
      onComplete?.();
    }
  }

  if (reducedMotion) {
    drawFrame(1);
    autoPlayDone = true;
    onComplete?.();
    return { destroy() {} };
  }

  const startTime = performance.now();
  rafId = requestAnimationFrame(animate);

  // ── Scroll-driven reverse shatter + fade-out ───────────────────────────
  // Once the forward auto-play completes, scrolling past ~15 % of the hero
  // reverses the shatter animation (shards fade out cleanly). The handler
  // NEVER removes itself — always alive to manage the reverse after auto-play.
  const hero = document.getElementById('hero');
  const heroH = hero instanceof HTMLElement ? hero.offsetHeight : window.innerHeight;
  /** @type {(() => void)|null} */
  let scrollHandler = null;
  /** Whether the scroll handler has been destroyed externally. */
  let handlerDestroyed = false;
  /** Last reverse-progress value painted (guards redundant full-canvas redraws). */
  let lastReverseP = -1;

  if (heroH) {
    scrollHandler = () => {
      if (handlerDestroyed) return;
      const ratio = Math.max(0, window.scrollY) / heroH;

      if (autoPlayDone) {
        // Clean shard-only rendering — no Phase 0 glow or Phase 1 flash.
        // Fast decay: 1 scroll % → 10 animation % ; the reverse completes
        // between 15 % and 25 % of hero scroll.
        const reverseP = ratio > 0.15
          ? Math.max(0, 1 - (ratio - 0.15) * 10)
          : 1;

        // Hot path: fully reversed → the canvas is empty and hidden. Redrawing
        // a full-screen shard canvas on EVERY scroll event (it never stopped,
        // even deep inside the runway) was the main scroll-jank source — skip
        // entirely until the value actually changes.
        if (reverseP <= 0) {
          if (lastReverseP <= 0) return;
          lastReverseP = 0;
          ctx.clearRect(0, 0, w, h);
          canvas.style.opacity = '0';
          return;
        }
        // No visual change since the last paint → nothing to redraw.
        if (reverseP === lastReverseP) return;

        lastReverseP = reverseP;
        // Must clearRect FIRST to erase the previous frame, otherwise shard
        // alpha accumulates white on every scroll tick.
        ctx.clearRect(0, 0, w, h);
        // Exit: amplified stagger so outer shards vanish fast → decisive
        // "shrink toward impact point" rather than a stretched fade.
        drawShardsAt(reverseP, true, 1.0);
        canvas.style.opacity = String(reverseP);
        return;
      }

      // Idle CSS fade (while auto-play runs). NEVER remove the handler —
      // it is needed for the reverse animation after autoPlayDone.
      canvas.style.opacity = String(Math.max(0, 1 - ratio * 2));
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    scrollHandler();
  }

  return {
    destroy() {
      if (rafId) cancelAnimationFrame(rafId);
      if (scrollHandler && !handlerDestroyed) {
        window.removeEventListener('scroll', scrollHandler, { passive: true });
        handlerDestroyed = true;
      }
    },
  };
}
